import React, { useEffect, useState } from 'react';
import select from '#lib/components/select.jsx';
import Dialog from '#lib/components/dialog.jsx';
import Button from '#lib/components/button.jsx';
import Input from '#lib/components/input.jsx';
import FloatingTooltip from 'src/components/tooltip.jsx';
import { QuestionMarkCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useAiDescription } from '#hooks/use-open-ai.js';
import { addClasses, cn } from '#root/lib/helpers/class.js';

// Dati API aiDescriptionMakers
// TODO: se cambiano lato API, aggiornarle
const aiLengthData = {
    SHORT: 'short',
    MEDIUM: 'medium',
    LONG: 'long'
};
const words = {
    short: 100,
    medium: 200,
    long: 300
};
const aiSentimentData = {
    SERIOUS: 'serious',
    NEUTRAL: 'neutral',
    ROMANTIC: 'romantic'
};

export function AiButton(props) {
    const { onClick, style, busy, className } = props;
    return (
        <Button
            className={cn(
                'cursor-pointer items-end h-[24px] px-2 transition-all duration-1000 ease-out bg-sky-500 hover:bg-sky-600 text-white rounded-md shadow-sm shadow-sky-900/50',
                className
            )}
            onClick={onClick}
            style={style}
            busy={busy}
        >
            <span className="inset-0 flex items-center justify-center gap-1">
                {_t.edit.planner.aiButton()}
                <SparklesIcon className="w-4 h-4" />
            </span>
        </Button>
    );
}

export default function AiDescription(props) {
    const {
        isOpen,
        setIsOpen,
        onClose,
        setAiDescription, // viene chiamata passandole la descrizione generata come stringa
        name,
        city,
        language,
        themeId,
        contextName
    } = props;

    const [sentiment, setSentiment] = useState(aiSentimentData.NEUTRAL);
    const [length, setLength] = useState(aiLengthData.MEDIUM);
    const [subject, setSubject] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');

    const lengthOptions = ['short', 'medium', 'long'];
    const sentimentOptions = ['serious', 'neutral', 'romantic'];

    const sentimentText = _t.dropdown.aiDescriptionSentiment[sentiment]();
    const basePrompt = _t.edit.planner.aiPromptBase(sentimentText, words[length]);
    const extraPrompt = `${name ? name?.trim() : ''}${
        city && typeof city === 'string' ? ' in ' + city?.split(',')[0]?.trim() : ''
    }`;

    const makePrompt = (subject) => (
        <span className={`min-h-[40px] overflow-wrap break-words`}>
            {basePrompt +
                ' ' +
                (subject?.length > 0
                    ? _t.edit.planner.aiPromptExtra(subject, 'prompt')
                    : _t.edit.planner.aiPromptExtra(extraPrompt, 'noprompt'))}
        </span>
    );

    const subjectPlaceholder =
        !name && !city ? "'" + _t.edit.fields.title() + "'" + ' in ' + "'" + _t.edit.fields.city() + "'" : extraPrompt;

    /////////////////////////////////////////////

    useEffect(() => {
        if (subject?.length > 200) return;
        const newPrompt = makePrompt(subject);
        setAiPrompt(newPrompt);
    }, [subject, name, city, sentiment, length]);

    const _onClose = () => {
        onClose && onClose();
        setSubject('');
        isOpen && setIsOpen((c) => !c);
    };

    const aiDescriptionMutation = useAiDescription({
        onSuccess: (res) => {
            setAiDescription(res.result);
            isOpen && _onClose();
        },
        onError: (e) => console.log(e)
    });

    return (
        <div className="flex flex-col gap-1 py-2 transition-all duration-500 ease-out">
            <div className="flex items-center justify-between h-6 h-min-6">
                <label className="text-sm text-neutral-700" htmlFor="description">
                    {_t.edit.description()}
                </label>
                <AiButton
                    isOpen={isOpen}
                    onClick={() => setIsOpen(!isOpen)}
                    busy={!isOpen && aiDescriptionMutation.isLoading}
                />
            </div>
            <Dialog
                isOpen={isOpen}
                onClose={_onClose}
                className="fixed z-50 flex items-center justify-center h-screen inset-4"
            >
                <Dialog.Panel className="flex flex-col justify-evenly min-w-[450px] w-[750px] z-50 bg-white border-solid border-2 ring-2 ring-sky-700 border-sky-300 rounded-md">
                    <div className="flex justify-between gap-2 px-8 pr-2">
                        <Dialog.CloseButton />
                    </div>
                    {
                        <div className={cn(`flex flex-col py-1 mb-2 justify-center`)}>
                            <div className="pt-2 text-xl font-bold text-center text-neutral-800">
                                {_t.edit.planner.aiDescriptionTitle()}
                            </div>
                            <div className={`min-w-[334px] flex flex-col items-center px-8 gap-2 w-full`}>
                                <div className="flex flex-col justify-center w-full gap-2 py-8">
                                    <Input.Full
                                        name="subject"
                                        className={'p-1'}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder={subjectPlaceholder}
                                        inputClassName={addClasses(
                                            cn(
                                                `ai w-full max-h-[35px] h-[35px]', ${
                                                    subject?.length > 0 ? '' : 'text-xs/[1.25rem]'
                                                }`
                                            )
                                        )}
                                        label={
                                            <>
                                                {_t.edit.planner.aiSubject()}
                                                <FloatingTooltip
                                                    content={_t.edit.planner.aiPromptInfo(contextName)}
                                                    placement="right"
                                                    className={addClasses('max-w-[380px] p-[10px] ')}
                                                    bgColor="#0090D2DD"
                                                >
                                                    <QuestionMarkCircleIcon
                                                        className={`z-[9999] cursor-default text-sky-700 hover:text-sky-800 w-5 h-5 -translate-x-[3px] translate-y-[5px]`}
                                                    />
                                                </FloatingTooltip>
                                            </>
                                        }
                                    />
                                    <div className="flex items-end justify-between gap-2 p-1">
                                        <select.Full
                                            className="max-w-[50%] w-[50%]"
                                            buttonClassName={addClasses('h-[35px]')}
                                            optionsClassName={addClasses('z-[9999] -mt-1')}
                                            label={_t.edit.planner.aiSentimentLabel()}
                                            options={sentimentOptions}
                                            value={sentiment}
                                            accessValue={(v) => v}
                                            displayValue={(v, options) => {
                                                if (!options.includes(v)) return v;
                                                return _t.dropdown.aiDescriptionSentiment[v]();
                                            }}
                                            inPortal={false}
                                            onChange={(v) => setSentiment(v)}
                                            bg="sky"
                                        />
                                        <select.Full
                                            className="max-w-[50%] w-[50%]"
                                            buttonClassName={addClasses('h-[35px]')}
                                            optionsClassName={addClasses('z-[9999] -mt-1')}
                                            label={_t.edit.planner.aiLengthLabel()}
                                            options={lengthOptions}
                                            value={length}
                                            accessValue={(v) => v}
                                            displayValue={(v, options) => {
                                                if (!options.includes(v)) return v;
                                                return _t.dropdown.aiDescriptionLength[v]();
                                            }}
                                            inPortal={false}
                                            onChange={(v) => setLength(v)}
                                            bg="sky"
                                        />
                                    </div>
                                    {(subject || city) && (
                                        <div className="w-full p-1 text-sm text-neutral-500">{aiPrompt}</div>
                                    )}
                                </div>
                                <div className="flex justify-center w-full">
                                    {/* GENERATE BUTTON */}
                                    <Button
                                        className={cn(
                                            'py-1 transition-all text-white px-1 font-semibold border rounded',
                                            !subject && !city
                                                ? 'cursor-default bg-sky-400'
                                                : 'cursor-pointer bg-sky-500 hover:bg-sky-600 border-blue-500'
                                        )}
                                        onClick={() => {
                                            aiDescriptionMutation.mutate({
                                                subject: subject || subjectPlaceholder,
                                                name,
                                                city,
                                                sentiment,
                                                length,
                                                language,
                                                themeId
                                            });
                                        }}
                                        busy={aiDescriptionMutation.isLoading}
                                    >
                                        {_t.global.generate()}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    }
                </Dialog.Panel>
            </Dialog>
        </div>
    );
}
